import { Button } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { ArrowIcon } from 'src/components/ArrowIcon';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography } from 'src/components/CustomTypography';
import { Information } from 'src/components/Information';
import { PerformanceRowsLimitTooltip } from 'src/components/PerformanceRowsLimitTooltip';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { flatConnectedAccounts } from 'src/data/performance';
import {
  getSubscriptionPlanLabel,
  isPlanFromBusinessCategory,
  isPlanFromBusinessOrEnterpriseCategory,
  SUBSCRIPTION_PLAN,
} from 'src/data/subscription';
import { useChannelAccounts, useGetUpgradeRowsLimitButtonAttributes } from 'src/services';
import { useCurrentCustomerPlan } from 'src/store';
import { formatNumber } from 'src/utils/format';
import { useUpgradePopup } from 'src/views/UpgradePopup';

import { BANNER_VARIANT } from './const';
import { useStyles } from './useStyles';

const TEXT_UPGRADE = 'Upgrade to power your generations with past performance data.';
const TEXT_TALKING_POINTS = 'Some talking points are missing from this integration!';
const getUsedRowsText = (plan, limit, isShortVersion = false) => {
  if (isShortVersion) {
    return `You've reached the ${formatNumber(limit)} performance data rows limit. Upgrade to ${
      isPlanFromBusinessCategory(plan) ? 'enterprise' : 'business'
    } tier to get insights for all of your copy.`;
  }
  return `You’ve used all of the ${formatNumber(limit)} allowed rows`;
};

export const PerformanceRowsLimitedBanner = ({
  className,
  variant,
  subscriptionPlan,
  upgradeUrl,
  rowsLimit,
  actionsOrigin,
}) => {
  const styles = useStyles();

  const currentPlan = useCurrentCustomerPlan();
  const { upgradeButtonText, shortUpgradeButtonText, isBusinessPlan } =
    useGetUpgradeRowsLimitButtonAttributes(upgradeUrl);
  const openUpgradePopup = useUpgradePopup({ actionsOrigin });

  const handleUpgradeClick = () => {
    openUpgradePopup(FEATURE_LIMITS.resourcesRowsPerCustomer);
  };
  const { connectedAccounts } = useChannelAccounts();
  const flattenConnectedAccounts = flatConnectedAccounts(connectedAccounts, true);

  if (isPlanFromBusinessOrEnterpriseCategory(currentPlan)) {
    return null;
  }

  return (
    <div
      className={cx(className, styles.root)}
      data-variant={variant}
    >
      <div className={styles.content}>
        {variant === BANNER_VARIANT.header && (
          <>
            <div className={styles.texts}>
              <CustomTypography
                className={styles.text}
                variant="regular14"
              >
                {getUsedRowsText(currentPlan, rowsLimit, true)}
              </CustomTypography>
            </div>

            <Button
              className={styles.buttonContained}
              onClick={handleUpgradeClick}
              variant="contained"
            >
              {upgradeButtonText}
            </Button>
          </>
        )}

        {variant === BANNER_VARIANT.banner && (
          <>
            <div className={styles.texts}>
              <div className={styles.row}>
                <CustomTypography
                  className={styles.text}
                  variant="regular14"
                >
                  {getUsedRowsText(currentPlan, rowsLimit, true)}
                </CustomTypography>

                <Information
                  className={styles.informationIcon}
                  content={
                    <PerformanceRowsLimitTooltip connectedAccounts={flattenConnectedAccounts} />
                  }
                />
              </div>
              <CustomTypography
                className={styles.text}
                variant="regular14"
              >
                {TEXT_UPGRADE}
              </CustomTypography>
            </div>

            <Button
              className={styles.buttonContained}
              onClick={handleUpgradeClick}
              variant="contained"
            >
              {upgradeButtonText}
            </Button>
          </>
        )}

        {variant === BANNER_VARIANT.medium && (
          <>
            <div className={styles.texts}>
              <CustomTypography
                className={styles.text}
                variant="medium14"
              >
                {TEXT_TALKING_POINTS}
              </CustomTypography>
              <CustomTypography
                className={styles.text}
                variant="regular12"
              >
                {getUsedRowsText(currentPlan, rowsLimit)} in your current plan (
                {getSubscriptionPlanLabel(subscriptionPlan)}).
              </CustomTypography>
            </div>

            <Button
              className={styles.buttonOutlined}
              onClick={handleUpgradeClick}
              variant="outlined"
            >
              {shortUpgradeButtonText}
            </Button>
          </>
        )}

        {variant === BANNER_VARIANT.small && (
          <div className={styles.texts}>
            <CustomTypography
              className={styles.text}
              variant="semiBold12"
            >
              {TEXT_TALKING_POINTS}
            </CustomTypography>

            <div className={styles.row}>
              <CustomTypography
                className={styles.text}
                variant="regular12"
              >
                {getUsedRowsText(currentPlan, rowsLimit)}.
              </CustomTypography>

              <Button
                className={styles.buttonText}
                onClick={handleUpgradeClick}
                variant="text"
              >
                {shortUpgradeButtonText}{' '}
                {!isBusinessPlan && <ArrowIcon className={styles.iconArrow} />}
              </Button>
            </div>
          </div>
        )}
      </div>

      {variant === BANNER_VARIANT.header && (
        <CustomTooltip
          title={<PerformanceRowsLimitTooltip connectedAccounts={flattenConnectedAccounts} />}
          arrow
        >
          <CustomTypography
            className={styles.labelRowsExplanation}
            variant="medium14"
          >
            What are rows?
          </CustomTypography>
        </CustomTooltip>
      )}
    </div>
  );
};

PerformanceRowsLimitedBanner.displayName = 'PerformanceRowsLimitedBanner';
PerformanceRowsLimitedBanner.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(BANNER_VARIANT)).isRequired,
  rowsLimit: PropTypes.number,
  subscriptionPlan: PropTypes.oneOf(Object.values(SUBSCRIPTION_PLAN)),
  upgradeUrl: PropTypes.string,
  actionsOrigin: PropTypes.string,
};
PerformanceRowsLimitedBanner.defaultProps = {
  className: '',
  rowsLimit: null,
  upgradeUrl: '',
  subscriptionPlan: null,
  actionsOrigin: '',
};
